/* ============================================================
   WALLQUEST PDP

   Swatch picker, gallery, family grouping, tearsheet disclosure,
   contract accordion, quantity stepper.

   Standalone on purpose — it does NOT use WSPdp. That layer models a
   made-to-order product with a colorway/material/photo triple and a
   variant matrix to match. Wallquest is a reseller line with one axis
   that matters, and borrowing the house state machine would mean
   carrying a material dimension that does not exist here.

   Everything reads the JSON payload emitted by wq-colorway-grid. That
   payload carries price fields ONLY when the visitor is an approved
   trade account, so this file must never assume they are present.

   Progressive enhancement: the page is fully rendered and correct before
   this runs. Nothing here creates content that did not already exist —
   it swaps what is shown.
   ============================================================ */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    var root = document.querySelector('[data-wq-pdp]');
    if (!root) return;

    var productId = root.dataset.productId;
    var payloadEl = document.getElementById('wq-colorways-' + productId);

    /* Products with no Color option emit no payload at all. The quantity
       stepper, tearsheet and accordion still apply, so those are wired
       regardless and only the colourway machinery is skipped. */
    var COLORWAYS = [];
    if (payloadEl) {
      try {
        COLORWAYS = JSON.parse(payloadEl.textContent) || [];
      } catch (err) {
        /* A malformed payload must not take the rest of the page with it.
           The server-rendered state is already correct and usable. */
        COLORWAYS = [];
      }
    }

    var byKey = {};
    COLORWAYS.forEach(function (cw) { byKey[cw.key] = cw; });

    /* ----------------------------------------------------------
       Money

       Formatted by Liquid, never here. Reimplementing the shop's format
       goes subtly wrong for anyone not on plain dollars, so the prefix
       and suffix are lifted off a price Liquid already rendered and the
       number swapped between them.

       Logged out there is no rendered price to read, and no price to
       display either, so this stays inert.
       ---------------------------------------------------------- */
    var priceEl = root.querySelector('[data-wq-price]');
    var PREFIX = '';
    var SUFFIX = '';
    if (priceEl) {
      var parts = priceEl.textContent.trim().match(/^(\D*)[\d.,]+(\D*)$/);
      if (parts) { PREFIX = parts[1]; SUFFIX = parts[2]; }
    }

    function money(cents) {
      var digits = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return PREFIX + digits + SUFFIX;
    }

    /* ----------------------------------------------------------
       State
       ---------------------------------------------------------- */
    var activeTile = root.querySelector('.wq-cw__tile[data-active]');
    var state = {
      key: activeTile ? activeTile.dataset.cwKey : (COLORWAYS[0] ? COLORWAYS[0].key : null),
      image: 0
    };

    function current() {
      return state.key ? byKey[state.key] : null;
    }

    /* ----------------------------------------------------------
       Gallery
       ---------------------------------------------------------- */
    var gallery = root.querySelector('[data-wq-gallery]');
    var mainImage = root.querySelector('[data-wq-main-image]');

    function renderGallery() {
      var cw = current();
      if (!cw || !gallery || !mainImage) return;

      var images = cw.images && cw.images.length ? cw.images : (cw.image ? [cw.image] : []);
      if (!images.length) return;

      var idx = Math.min(state.image, images.length - 1);
      mainImage.src = images[idx];
      mainImage.removeAttribute('srcset');
      mainImage.alt = cw.name ? cw.name + ' — ' + document.title : mainImage.alt;

      /* The thumb row exists only above one image. Building it here rather
         than toggling a server-rendered row keeps the "exactly one image
         means no row" rule in one place. */
      var thumbs = gallery.querySelector('[data-wq-thumbs]');
      if (images.length < 2) {
        if (thumbs) thumbs.remove();
        return;
      }
      if (!thumbs) {
        thumbs = document.createElement('div');
        thumbs.className = 'wq-gallery__thumbs';
        thumbs.setAttribute('data-wq-thumbs', '');
        gallery.appendChild(thumbs);
      }
      thumbs.textContent = '';
      images.forEach(function (src, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'wq-gallery__thumb';
        b.setAttribute('data-wq-thumb', String(i));
        if (i === idx) {
          b.setAttribute('data-active', '');
          b.setAttribute('aria-current', 'true');
        }
        b.setAttribute('aria-label', 'View image ' + (i + 1));
        var img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 84;
        img.height = 84;
        b.appendChild(img);
        thumbs.appendChild(b);
      });
    }

    /* ----------------------------------------------------------
       Stock

       The field is empty on all 18,829 variants until the sweep lands.
       Absent is the designed state, not a fallback: no dot, no badge, no
       claim, and never an assumed "In Stock". Samples carry no status and
       must not inherit the roll's.

       There is deliberately no "Out of Stock" — Wallquest accept
       backorders on everything, so the third state is an invitation.
       ---------------------------------------------------------- */
    var stockRow = root.querySelector('[data-cw-stock]');
    var stockDot = root.querySelector('[data-cw-dot]');
    var stockLabel = root.querySelector('[data-cw-stock-label]');
    var backorder = root.querySelector('[data-cw-backorder]');
    var freshness = root.querySelector('[data-wq-freshness]');
    var freshnessAgo = root.querySelector('[data-wq-freshness-ago]');

    function relativeTime(iso) {
      var then = Date.parse(iso);
      if (isNaN(then)) return '';
      var days = Math.floor((Date.now() - then) / 86400000);
      if (days <= 0) return 'today';
      if (days === 1) return 'yesterday';
      if (days < 30) return days + ' days ago';
      var months = Math.floor(days / 30);
      return months === 1 ? 'last month' : months + ' months ago';
    }

    function renderStock() {
      var cw = current();
      var status = cw && cw.stock ? String(cw.stock).trim() : '';

      if (stockRow) {
        if (!status) {
          stockRow.hidden = true;
        } else {
          stockRow.hidden = false;
          if (stockDot) stockDot.setAttribute('data-state', status);
          if (stockLabel) {
            stockLabel.textContent = status === 'Backorder' ? 'Backorder — orderable' : status;
          }
        }
      }

      if (backorder) backorder.hidden = status !== 'Backorder';

      if (freshness) {
        var ago = cw && cw.checkedAt ? relativeTime(cw.checkedAt) : '';
        if (!ago) {
          freshness.hidden = true;
        } else {
          freshness.hidden = false;
          if (freshnessAgo) freshnessAgo.textContent = ago;
        }
      }
    }

    /* ----------------------------------------------------------
       Info, form fields and price
       ---------------------------------------------------------- */
    var selectedNameEls = root.querySelectorAll('[data-cw-selected], [data-wq-selected-name]');
    var skuEl = root.querySelector('[data-wq-sku]');
    var variantIdEl = root.querySelector('[data-wq-variant-id]');
    var sampleIdEl = root.querySelector('[data-wq-sample-id]');
    var sampleForm = root.querySelector('#wq-sample-form');
    var lineTotalEl = root.querySelector('[data-wq-line-total]');
    var qtyInput = root.querySelector('[data-wq-qty]');
    var buyBox = root.querySelector('[data-wq-buy]');
    var minQty = buyBox ? parseInt(buyBox.dataset.minQty, 10) || 1 : 1;

    function renderInfo() {
      var cw = current();
      if (!cw) return;

      var lifecycle = root.querySelector('[data-wq-lifecycle]');
      if (lifecycle) {
        lifecycle.textContent = cw.lifecycleNotice || '';
        lifecycle.hidden = !cw.lifecycleNotice;
      }

      selectedNameEls.forEach(function (el) {
        /* The colourway label carries an em dash prefix; the sample copy
           does not. Preserving whichever the server rendered avoids
           hard-coding punctuation in two places. */
        el.textContent = el.hasAttribute('data-cw-selected') ? '— ' + cw.name : cw.name;
      });

      if (skuEl) skuEl.textContent = cw.sku || '';
      if (variantIdEl && cw.sellableId) variantIdEl.value = cw.sellableId;

      /* Two products have no sample variant. Rather than leave a button
         wired to nothing, the row is removed. */
      if (sampleIdEl) {
        if (cw.sampleId) {
          sampleIdEl.value = cw.sampleId;
          if (sampleForm) sampleForm.hidden = false;
        } else if (sampleForm) {
          sampleForm.hidden = true;
        }
      }

      /* `price` (cents) and `priceFormatted` exist only inside the trade
         gate. Logged out both are absent and there is no price element to
         write to either, so this is a no-op rather than a guard. */
      if (priceEl && cw.priceFormatted) priceEl.textContent = cw.priceFormatted;
      else if (priceEl && typeof cw.price === 'number') priceEl.textContent = money(cw.price);

      renderTotal();
    }

    function renderTotal() {
      if (!lineTotalEl || !qtyInput) return;
      var cw = current();
      /* No-Color products have no colourway payload. Their resolved unit
         price is rendered on the buy box, inside the same trade gate. */
      var unitPrice = cw ? cw.price : (buyBox ? Number(buyBox.dataset.unitPrice) : NaN);
      if (typeof unitPrice !== 'number' || !Number.isFinite(unitPrice)) return;
      var qty = Math.max(minQty, parseInt(qtyInput.value, 10) || minQty);
      lineTotalEl.textContent = money(unitPrice * qty);
    }

    /* ----------------------------------------------------------
       Swatch grid: selection, collapse, family grouping
       ---------------------------------------------------------- */
    var grid = root.querySelector('[data-cw-grid]');
    var groupsWrap = root.querySelector('[data-cw-groups]');
    var toggle = root.querySelector('[data-cw-toggle]');
    var cwRoot = root.querySelector('[data-wq-colorways]');
    var expanded = false;

    function markActive() {
      root.querySelectorAll('.wq-cw__tile').forEach(function (t) {
        var on = t.dataset.cwKey === state.key;
        t.toggleAttribute('data-active', on);
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function select(key, opts) {
      if (!byKey[key]) return;
      state.key = key;
      state.image = 0;            /* swatch change resets the gallery */
      markActive();
      renderGallery();
      renderStock();
      renderInfo();

      /* Keep ?variant= honest so share links and the tearsheet link both
         point at what is actually on screen. replaceState, not pushState —
         picking through twenty colourways should not bury the back button. */
      var cw = current();
      if (cw && cw.sellableId && !(opts && opts.silent)) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', cw.sellableId);
        history.replaceState({}, '', url);
      }
      syncTearsheetHref();
    }

    if (grid) {
      grid.addEventListener('click', function (e) {
        var tile = e.target.closest('.wq-cw__tile');
        if (tile && tile.dataset.cwKey) select(tile.dataset.cwKey);
      });
    }

    /* Expanded view groups by colour family, in first-appearance order.
       Variants with no family form an unheaded group rendered FIRST — it
       is never labelled "Other", which would read as a colour family that
       does not exist. */
    function buildGroups() {
      if (!groupsWrap || !grid) return;

      var order = [];
      var buckets = {};
      COLORWAYS.forEach(function (cw) {
        var fam = (cw.family || '').trim();
        if (!buckets[fam]) { buckets[fam] = []; order.push(fam); }
        buckets[fam].push(cw);
      });

      /* Unheaded group first, whatever position it appeared in. */
      order.sort(function (a, b) {
        if (a === '') return -1;
        if (b === '') return 1;
        return 0;
      });

      var frag = document.createDocumentFragment();
      order.forEach(function (fam) {
        var group = document.createElement('div');
        if (fam) {
          var h = document.createElement('p');
          h.className = 'wq-cw__group-label';
          h.textContent = fam;
          group.appendChild(h);
        }
        var g = document.createElement('div');
        g.className = 'wq-cw__grid';
        buckets[fam].forEach(function (cw) {
          g.appendChild(makeTile(cw));
        });
        group.appendChild(g);
        frag.appendChild(group);
      });

      groupsWrap.textContent = '';
      groupsWrap.appendChild(frag);
      markActive();
    }

    function makeTile(cw) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'wq-cw__tile';
      b.setAttribute('data-cw-tile', cw.name);
      b.setAttribute('data-cw-key', cw.key);
      b.title = cw.name;
      b.setAttribute('aria-label', cw.name);
      b.setAttribute('aria-pressed', cw.key === state.key ? 'true' : 'false');
      if (cw.key === state.key) b.setAttribute('data-active', '');
      if (cw.image) {
        var img = document.createElement('img');
        img.src = cw.image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 52;
        img.height = 52;
        b.appendChild(img);
      }
      var ring = document.createElement('span');
      ring.className = 'wq-cw__ring';
      ring.setAttribute('aria-hidden', 'true');
      b.appendChild(ring);
      return b;
    }

    if (toggle && cwRoot) {
      var flatGrid = grid;
      toggle.addEventListener('click', function () {
        expanded = !expanded;
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (expanded) {
          buildGroups();
          cwRoot.removeAttribute('data-collapsed');
          toggle.textContent = 'Show fewer';
        } else {
          groupsWrap.textContent = '';
          groupsWrap.appendChild(flatGrid);
          cwRoot.setAttribute('data-collapsed', '');
          toggle.textContent = 'Show all ' + cwRoot.dataset.count + ' colourways';
          markActive();
        }
      });

      /* Delegated on the wrapper so regrouped tiles stay clickable. */
      groupsWrap.addEventListener('click', function (e) {
        var tile = e.target.closest('.wq-cw__tile');
        if (tile && tile.dataset.cwKey) select(tile.dataset.cwKey);
      });
    }

    /* ----------------------------------------------------------
       Gallery thumbs
       ---------------------------------------------------------- */
    if (gallery) {
      gallery.addEventListener('click', function (e) {
        var thumb = e.target.closest('[data-wq-thumb]');
        if (!thumb) return;
        state.image = parseInt(thumb.dataset.wqThumb, 10) || 0;
        renderGallery();
      });
    }

    /* ----------------------------------------------------------
       Quantity stepper

       Minimum comes from the server, which sets 5 for per-yard products
       and 1 otherwise. Enforced on blur as well as on the buttons, since
       the input is directly editable.
       ---------------------------------------------------------- */
    var stepper = root.querySelector('[data-wq-stepper]');
    if (stepper && qtyInput) {
      stepper.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-wq-step]');
        if (!btn) return;
        var next = (parseInt(qtyInput.value, 10) || minQty) + Number(btn.dataset.wqStep);
        qtyInput.value = Math.max(minQty, next);
        renderTotal();
      });
      qtyInput.addEventListener('input', renderTotal);
      qtyInput.addEventListener('blur', function () {
        qtyInput.value = Math.max(minQty, parseInt(qtyInput.value, 10) || minQty);
        renderTotal();
      });
    }

    /* ----------------------------------------------------------
       Tearsheet disclosure
       ---------------------------------------------------------- */
    var tsRoot = root.querySelector('[data-wq-tearsheet]');
    var tsToggle = tsRoot && tsRoot.querySelector('.wq-ts__toggle');
    var tsPanel = tsRoot && tsRoot.querySelector('.wq-ts__panel');
    var tsInput = root.querySelector('[data-ts-project]');
    var tsOpen = root.querySelector('[data-ts-open]');
    var tsColorway = root.querySelector('[data-ts-colorway]');

    function syncTearsheetHref() {
      if (!tsOpen) return;
      var base = tsOpen.dataset.tsBase;
      if (!base) return;
      var cw = current();
      var url = base + '?view=tearsheet&print=1';
      if (cw && cw.sellableId) url += '&variant=' + cw.sellableId;
      var project = tsInput && tsInput.value.trim();
      if (project) url += '&project=' + encodeURIComponent(project);
      tsOpen.href = url;
      if (tsColorway && cw && cw.name) tsColorway.textContent = cw.name;
    }

    if (tsToggle && tsPanel) {
      tsToggle.addEventListener('click', function () {
        var open = tsPanel.hidden;
        tsPanel.hidden = !open;
        tsToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open && tsInput) tsInput.focus();
      });
    }
    if (tsInput) tsInput.addEventListener('input', syncTearsheetHref);

    /* ----------------------------------------------------------
       Contract accordion
       ---------------------------------------------------------- */
    var contract = root.querySelector('[data-wq-contract]');
    if (contract) {
      var cToggle = contract.querySelector('.wq-contract__toggle');
      var cPanel = contract.querySelector('.wq-contract__grid');
      if (cToggle && cPanel) {
        cToggle.addEventListener('click', function () {
          var open = cPanel.hidden;
          cPanel.hidden = !open;
          cToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          var icon = cToggle.querySelector('.wq-contract__icon');
          if (icon) icon.textContent = open ? '−' : '+';
        });
      }
    }

    /* ----------------------------------------------------------
       Similar designs

       Fetched here rather than through Dawn's <product-recommendations>,
       which loads on an IntersectionObserver. The band is genuinely
       below the fold, so lazy is the right instinct — but an empty mount
       is a zero-height box, and an observer on a box with no area is a
       coin flip across browsers and a hard thing to verify. The band
       exists on 88.6% of products, so it is going to load on almost
       every view anyway; deferring it to idle costs nothing and always
       happens.

       Fires once. A failed fetch leaves the mount empty, which is the
       same state as the 56 products that have no recommendations — the
       page is complete without it either way.
       ---------------------------------------------------------- */
    var similarMount = document.querySelector('[data-wq-similar]');
    if (similarMount && similarMount.dataset.sectionId) {
      var loadSimilar = function () {
        var url = '/recommendations/products?section_id=' + encodeURIComponent(similarMount.dataset.sectionId) +
          '&product_id=' + encodeURIComponent(similarMount.dataset.productId) +
          '&limit=' + encodeURIComponent(similarMount.dataset.limit || 4) +
          '&intent=related';
        fetch(url)
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) {
            if (!html) return;
            var box = document.createElement('div');
            box.innerHTML = html;
            /* The response is the whole section, mount and all. Take the
               band out of it rather than nesting a mount in a mount. */
            var band = box.querySelector('.wq-similar');
            if (band) similarMount.appendChild(band);
          })
          .catch(function () { /* leave the mount empty */ });
      };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadSimilar, { timeout: 2000 });
      } else {
        setTimeout(loadSimilar, 200);
      }
    }

    /* ----------------------------------------------------------
       First paint

       Synchronize the main form and its displayed price with the resolved
       sellable variant, including entry through a sample link. Keep the
       original URL so sample/order links still select the same colourway.
       ---------------------------------------------------------- */
    renderStock();
    renderInfo();
    renderTotal();
    syncTearsheetHref();
    if (state.key && byKey[state.key]) markActive();
  });
})();
